// PHA-1225: the patient chart's "Actions" menu (top right of the chart header). It sends the
// Program Confirmation Form (a text with a link to a Yes/No form), at any stage, usually right
// after a call where the patient agreed verbally. It comes on top of the verbal consent; it
// never changes Consent Status. It goes in the patient's preferred language unless the care
// team picks another of the clinic's languages.
// On dev this menu also opens the PHA-626 onboarding script; that is not promoted yet.
import React, { useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  InputLabel,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Select,
} from '@mui/material';
import { ArrowDropDown, Sms } from '@mui/icons-material';
import { toast } from 'react-hot-toast';
import { patientApi } from '../services/api';
import { CONSENT_FORM_TITLE, ConsentWording, hasOwnConsentWording } from '../utils/consentText';

const ENGLISH = 'en-US';

interface Props {
  patientId: string;
  patient: { first_name?: string | null };
  /** For the consent-text confirmation. */
  phoneNumber?: string | null;
  phoneType?: string | null;
  /** The patient's preferred language, and the clinic's languages with their form wording. */
  patientLanguage?: string | null;
  supportedLanguages?: Array<{ code: string; name: string } & ConsentWording>;
  onConsentSent?: () => void;
}

const PatientActionsMenu: React.FC<Props> = ({
  patientId,
  patient,
  phoneNumber,
  phoneType,
  patientLanguage,
  supportedLanguages,
  onConsentSent,
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [consentConfirmOpen, setConsentConfirmOpen] = useState(false);
  const [sendingConsent, setSendingConsent] = useState(false);
  const [consentLanguage, setConsentLanguage] = useState(ENGLISH);
  const isLandline = phoneType === 'landline';
  const languages = supportedLanguages?.length ? supportedLanguages : [{ code: ENGLISH, name: 'English' }];
  const pickedLanguage = languages.find((l) => l.code === consentLanguage);
  const sendsEnglishInstead = consentLanguage !== ENGLISH && !hasOwnConsentWording(pickedLanguage);

  const openConsentConfirm = () => {
    setAnchorEl(null);
    // Default: the patient's preferred language, when the clinic supports it.
    const preferred = patientLanguage && languages.some((l) => l.code === patientLanguage) ? patientLanguage : ENGLISH;
    setConsentLanguage(preferred);
    setConsentConfirmOpen(true);
  };

  const sendConsent = async () => {
    setSendingConsent(true);
    try {
      // No picker shown (one language, or the list didn't load): let the server use the
      // patient's preferred language.
      await patientApi.sendConsentRequest(patientId, languages.length > 1 ? consentLanguage : undefined);
      toast.success(`${CONSENT_FORM_TITLE} sent.`);
      setConsentConfirmOpen(false);
      onConsentSent?.();
    } catch (err: any) {
      // The backend's message is the actionable part (landline, opted out, no number).
      toast.error(err?.message || `Failed to send the ${CONSENT_FORM_TITLE}`);
      onConsentSent?.();
    } finally {
      setSendingConsent(false);
    }
  };

  return (
    <>
      <Button
        variant="outlined"
        size="small"
        endIcon={<ArrowDropDown />}
        onClick={(e) => setAnchorEl(e.currentTarget)}
        aria-haspopup="menu"
        aria-controls={anchorEl ? 'patient-actions-menu' : undefined}
        data-testid="patient-actions-menu"
      >
        Actions
      </Button>
      <Menu
        id="patient-actions-menu"
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem onClick={openConsentConfirm} data-testid="send-consent-button">
          <ListItemIcon><Sms fontSize="small" /></ListItemIcon>
          <ListItemText>Send {CONSENT_FORM_TITLE}</ListItemText>
        </MenuItem>
      </Menu>
      <Dialog open={consentConfirmOpen} onClose={() => !sendingConsent && setConsentConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Send {CONSENT_FORM_TITLE}?</DialogTitle>
        <DialogContent>
          {isLandline ? (
            <DialogContentText>
              This patient's phone is a landline, so a text cannot be sent. Record consent by phone
              instead.
            </DialogContentText>
          ) : (
            <>
              <DialogContentText>
                {patient.first_name || 'The patient'} will get a text{phoneNumber ? ` at ${phoneNumber}` : ''} with
                a link to a short Yes/No form, in your clinic's wording. Their answer will show here on
                the chart. It does not change the Consent Status.
              </DialogContentText>
              {languages.length > 1 && (
                <FormControl fullWidth size="small" sx={{ mt: 2 }}>
                  <InputLabel id="consent-language-label">Language</InputLabel>
                  <Select
                    labelId="consent-language-label"
                    label="Language"
                    value={consentLanguage}
                    onChange={(e) => setConsentLanguage(String(e.target.value))}
                    disabled={sendingConsent}
                    data-testid="send-consent-language"
                  >
                    {languages.map((l) => (
                      <MenuItem key={l.code} value={l.code}>
                        {l.name}{l.code === patientLanguage ? " (patient's language)" : ''}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
              {sendsEnglishInstead && (
                <DialogContentText sx={{ mt: 1, fontSize: '0.85rem' }} data-testid="send-consent-english-note">
                  Your clinic has no {pickedLanguage?.name ?? consentLanguage} wording yet, so the English
                  wording will be sent.
                </DialogContentText>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConsentConfirmOpen(false)} disabled={sendingConsent}>Cancel</Button>
          {!isLandline && (
            <Button
              variant="contained"
              onClick={sendConsent}
              disabled={sendingConsent}
              data-testid="send-consent-confirm"
            >
              {sendingConsent ? 'Sending…' : 'Send text'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};

export default PatientActionsMenu;
